import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
} from '@angular/core';
import { scaleLinear } from 'd3-scale';
import * as moment from 'moment';
import { AnchoredPosition } from '../callout/callout-position';
import { Size } from '../core/size.service';

export enum Interval {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

const DATE_INTERVAL_FORMAT_MAP = {
  day: 'M/D',
  week: 'M/D',
  month: 'MMM',
  year: 'YYYY',
};

const MIN_X_TICKS = 1;
const Y_TICKS = 5;
const DAYS_PER_WEEK = 7;
const DAYS_PER_MONTH = 30;
const LEFT_OFFSET = 64;
const BOTTOM_OFFSET = 20;
const RIGHT_OFFSET = 40;
const EXTRA_OFFSET = 100;
const Y_DOMAIN_OFFSET = 1.25;
const BAR_WIDTH = 20;
const BAR_WIDTH_WITH_MARGIN = 75;
const SVG_WIDTH_PER_BUCKET_FOR_OFFSET = 175;

export type KeyLegendEntry = { key: string; color: string };
export type KeyLegend = KeyLegendEntry[];

@Component({
  selector: 'vertical-bar',
  templateUrl: './vertical-bar.component.html',
  styleUrls: ['./vertical-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerticalBarComponent<T> {
  svgSize: Size = { width: 1, height: 1 };
  buckets: Map<number, T[]>;
  bars: {
    values: number[];
    x: number;
    yArr: number[];
    w: number;
    hArr: number[];
    dateMoment: number;
  }[];
  xInterval: Interval;
  maxXTickCount: number;

  @Input()
  data: T[] = [];

  @Input()
  keyLegend: KeyLegend = [];

  @Input()
  calculateDate: (datum: T) => moment.Moment = () => moment();

  @Input()
  calculateValue: (data: T[]) => number[] = () => [1];

  @Input()
  dateFormatter: (date: moment.Moment) => string = date => date.toString();

  @Input()
  valueFormatter: (value: number) => string = value => {
    if (!value) return '';
    // If there is a decimal in the value, then restrict the number of decimal points
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  };

  @Output() currentHoverable: {
    values: number[];
    dateMoment: number;
  } | null = null;

  @Output() calloutAnchor: DOMRect = new DOMRect(0, 0, 0, 0);

  readonly AnchoredPosition = AnchoredPosition;

  public onHover(value: any) {
    const temp = this.currentHoverable;
    this.currentHoverable = { ...temp, ...value };
  }

  public setYHoverable(event: MouseEvent) {
    this.calloutAnchor = new DOMRect(
      event.clientX - 12,
      event.clientY - 12,
      24,
      24,
    );
  }

  public onLeave() {
    this.currentHoverable = null;
  }

  hasData() {
    return (
      this.data &&
      this.data.length &&
      this.calculateValue(this.data).some(v => v > 0)
    );
  }

  setXInterval(minDate: moment.Moment, maxDate: moment.Moment): Interval {
    const diff = maxDate.diff(minDate, 'days');
    const barCount = this.getMaxXTickCount();
    if (diff >= barCount * DAYS_PER_MONTH) {
      this.xInterval = Interval.YEAR;
    } else if (diff >= barCount * DAYS_PER_WEEK) {
      this.xInterval = Interval.MONTH;
    } else if (diff > barCount) {
      this.xInterval = Interval.WEEK;
    } else {
      this.xInterval = Interval.DAY;
    }
    return this.xInterval;
  }

  getXScale() {
    const buckets = this.getBuckets();
    const leftOffsetExtra =
      buckets.size === 1
        ? this.svgSize.width / 2 - BAR_WIDTH / 2 - LEFT_OFFSET
        : this.shouldAddHorizontalOffset()
          ? EXTRA_OFFSET
          : 0;
    const rightOffset = leftOffsetExtra + RIGHT_OFFSET;
    return scaleLinear()
      .domain([0, buckets.size - 1])
      .range([LEFT_OFFSET + leftOffsetExtra, this.svgSize.width - rightOffset]);
  }

  getXTicks() {
    const scale = this.getXScale();
    const [minValue, maxValue] = scale.domain();
    return scale.ticks(
      Math.max(
        Math.min(Math.round(maxValue - minValue), this.getMaxXTickCount()),
        MIN_X_TICKS,
      ),
    );
  }

  getBuckets() {
    const minDate = moment(this.calculateDate(this.data[0]));
    const maxDate = moment(this.calculateDate(this.data[this.data.length - 1]));
    const interval = this.setXInterval(minDate, maxDate);
    const date = minDate.clone().startOf(interval);
    this.buckets = new Map<number, T[]>();

    while (date.isBefore(maxDate) || date.isSame(maxDate)) {
      this.buckets.set(date.valueOf(), []);
      date.add(1, interval);
    }

    for (const datum of this.data) {
      const key = moment(this.calculateDate(datum)).startOf(interval).valueOf();
      const bucket = this.buckets.get(key);
      if (bucket) {
        bucket.push(datum);
      }
    }

    return this.buckets;
  }

  getYScale() {
    const buckets = this.getBuckets();
    let maxY = 0;

    buckets.forEach(b => {
      const bucketValue = this.calculateValue(b);
      const bucketTotal = bucketValue.reduce(
        (total, datum) => total + datum,
        0,
      );
      if (bucketTotal > maxY) maxY = bucketTotal;
    });

    return (
      scaleLinear()
        // Some padding was added to the domain so the top values weren't cut off
        .domain([0, maxY * Y_DOMAIN_OFFSET])
        .range([this.svgSize.height - BOTTOM_OFFSET, 0])
    );
  }

  getYTicks() {
    return this.getYScale().ticks(Y_TICKS);
  }

  getBars() {
    const buckets = this.getBuckets();
    const xScale = this.getXScale();
    const yScale = this.getYScale();
    this.bars = [];
    let bucketI = 0;
    buckets.forEach((b, key) => {
      // Reverse the bar order to make rendering it in the html simpler because we draw them from bottom to top
      const values = this.calculateValue(b).reverse();
      const hArr = values.map(v => Math.max(yScale(0)! - yScale(v)!, 0));
      let barSplitI = 0;
      let currHOffset = 0;
      this.bars.push({
        values,
        x: xScale(bucketI++)! - BAR_WIDTH / 2,
        yArr: values.map(v => {
          currHOffset += barSplitI > 0 ? hArr[barSplitI - 1] : 0;
          const currY = yScale(v)! - currHOffset;
          barSplitI++;
          return currY;
        }),
        w: BAR_WIDTH,
        hArr,
        dateMoment: key,
      });
    });
    return this.bars;
  }

  minZero(n: number | undefined): number {
    return Math.max(n ?? 0, 0);
  }

  getDateRange(bucketI: number) {
    let range = moment(this.getBars()[bucketI].dateMoment).format(
      DATE_INTERVAL_FORMAT_MAP[this.xInterval],
    );
    if (this.xInterval === Interval.WEEK)
      range += ` - ${moment(this.getBars()[bucketI].dateMoment)
        .add(1, 'week')
        .format(DATE_INTERVAL_FORMAT_MAP[this.xInterval])}`;
    return range;
  }

  getIndTooltip(bar: { values: number[] }, value: number) {
    const tooltip = `${this.keyLegend[value].key}: ${this.valueFormatter(
      bar.values[this.keyLegend.length - value - 1],
    )}`;

    return tooltip;
  }

  getTooltipTitle(dateMoment: number) {
    return moment(dateMoment).format('MMM DD YYYY');
  }

  shouldAddHorizontalOffset(): boolean {
    return (
      (this.svgSize.width - LEFT_OFFSET) / this.getBuckets().size >
      SVG_WIDTH_PER_BUCKET_FOR_OFFSET
    );
  }

  private getMaxXTickCount(): number {
    if (this.maxXTickCount > 0) return this.maxXTickCount;
    this.maxXTickCount = Math.floor(
      (this.svgSize.width - LEFT_OFFSET) / BAR_WIDTH_WITH_MARGIN,
    );
    return this.maxXTickCount;
  }
}
